import type { Service } from '../graphqlEnums'

/**
 * Networks whose backend gallery-mix restrictions (`noMixedMediaRule`,
 * `singleVideoRule`) are lifted by the org-level `mixed-media` Split flag.
 * Every other network applies those rules unconditionally.
 * @see domains/updates-domain/src/use-cases/create/validators/services/twitter/rules.ts
 * @see domains/updates-domain/src/use-cases/create/validators/services/threads/rules.ts
 * @see domains/updates-domain/src/use-cases/create/validators/services/instagram/rules.ts
 */
export const MIXED_MEDIA_CAPABLE_SERVICES: ReadonlySet<Service> = new Set([
  'instagram',
  'threads',
  'twitter',
])

export type MixedMediaCapability = {
  canMixMediaTypes: boolean
  canHaveMultipleVideos: boolean
}

/**
 * Frontend mirror of the backend `galleryMixRules` gating — the backend
 * remains the enforcement authority, this only predicts its outcome so the
 * composer can decide what to let a user attempt.
 *
 * Both fields are true only when every gate holds: the org flag is on and
 * the network is on the capable list. They are kept separate because the
 * backend could diverge them per network later, even though both derive from
 * the same gating today.
 *
 * @see MIXED_MEDIA_CAPABLE_SERVICES
 */
export function getMixedMediaCapability(input: {
  orgEnabled: boolean
  service: Service
}): MixedMediaCapability {
  const capable =
    input.orgEnabled && MIXED_MEDIA_CAPABLE_SERVICES.has(input.service)

  return {
    canMixMediaTypes: capable,
    canHaveMultipleVideos: capable,
  }
}
